import React from 'react';
import PropTypes from 'prop-types';
import { Switch, Route, withRouter, Redirect } from 'react-router-dom';
import _ from 'lodash';

import EvaluateAbilitiesVisualIntro from './EvaluateAbilitiesVisualIntro';
import EvaluateAbilitiesVisualBackground from './EvaluateAbilitiesVisualBackground';
import EvaluateAbilitiesVisualIssues from './EvaluateAbilitiesVisualIssues';
import EvaluateAbilitiesVisualWrapup from './EvaluateAbilitiesVisualWrapup';
import { SUBTABS, VISUAL_STEPS } from './ConstPaths';

//Manage the wizard form for the Evaluate Abilities / Visual sub-tab
//Initialize the forms with props and manage those as local state.
//On Continue, update the global Session state with the local state from the forms. 
// Following pattern described in https://goshacmd.com/wizard-form-collect-info/

const propTypes = {
  firstName: PropTypes.string,
  pronouns: PropTypes.arrayOf(PropTypes.string),
  wearsGlasses: PropTypes.string,
  betterWithGlasses: PropTypes.string,
  hasOtherVisualAids: PropTypes.string,
  visualAidsNotes: PropTypes.string,
  formalEvalNotes: PropTypes.string,
  visionIssues: PropTypes.arrayOf(PropTypes.string),
  issuesNotes: PropTypes.string,
  updateSession: PropTypes.func.isRequired
};

const defaultProps = {
  firstName: 'the client',
  pronouns: ['they', 'them', 'their'],
  wearsGlasses: '',
  betterWithGlasses: '',
  hasOtherVisualAids: '',
  visualAidsNotes: '',
  formalEvalNotes: '',
  visionIssues: [],
  issuesNotes: ''
};

const BASE_URL = SUBTABS.VISUAL + "/";
//the steps of the wizard form:
const steps = VISUAL_STEPS;

const NEXT_SECTION = SUBTABS.COGNITION;

class EvaluateAbilitiesVisual extends React.Component {
  constructor(props) {
    super(props);
    //manage the incoming values as local state
    this.state = {
      wearsGlasses: this.props.wearsGlasses,
      betterWithGlasses: this.props.betterWithGlasses,
      hasOtherVisualAids: this.props.hasOtherVisualAids,
      visualAidsNotes: this.props.visualAidsNotes,
      formalEvalNotes: this.props.formalEvalNotes,
      visionIssues: this.props.visionIssues ?? [],
      issuesNotes: this.props.issuesNotes
    };

    this.saveVisualBackgroundData = this.saveVisualBackgroundData.bind(this);
    this.saveVisionIssuesData = this.saveVisionIssuesData.bind(this);

    this.saveBetterWithGlasses = this.saveBetterWithGlasses.bind(this);
    this.saveFormalEvalNotes = this.saveFormalEvalNotes.bind(this);
    this.saveHasOtherVisualAids = this.saveHasOtherVisualAids.bind(this);
    this.saveIssuesNotes = this.saveIssuesNotes.bind(this);
    this.saveVisionIssues = this.saveVisionIssues.bind(this);
    this.saveVisualAidsNotes = this.saveVisualAidsNotes.bind(this);
    this.saveWearsGlasses = this.saveWearsGlasses.bind(this);

    this.onContinue = this.onContinue.bind(this);
  }

  //Get the current step from the URL
  getCurrentStep(){
    //e.g. URL of '/gather-info/goals/intro' would have step of 'intro'
    const stepParam = this.props.match.params.step;
    if(stepParam){
      console.log("EvalAbilVisual.getCurrentStep: got step from URL");
      return(stepParam);
    }
    else{
      console.log("EvalAbilVisual.getCurrentStep: using default step");
      return(steps.INTRO);
    }
  }

  //On Continue for a given step:
  // figure out the next step and go to it
  // and if we're totally done with this section, submit the data.
  onContinue() {
    let history = this.props.history;
    let nextStep = '';
    let nextRoute = '';
    let goToNextSection = false;
    const curURL = new URL(window.location.href);


    switch(this.getCurrentStep()){
      case steps.INTRO:
        nextStep = steps.BACKGROUND;
        break;
      case steps.BACKGROUND:
        nextStep = steps.ISSUES;
        break;
      case steps.ISSUES:
        nextStep = steps.WRAPUP;
        break;
      case steps.WRAPUP:
        nextStep = '';
        goToNextSection = true;
        break;
      default:
        console.log("   default next step");
        nextStep = steps.INTRO;
    }
    if (!goToNextSection) {
      nextRoute = BASE_URL + nextStep;
    } 
    else {
      // alert("Done with this section. Data saved are: " + JSON.stringify(this.state));
      nextRoute = NEXT_SECTION;
    }
    console.log("    pushing new route: " + nextRoute);
    history.push({
      pathname: nextRoute,
      search: curURL.search
    });
  }

  saveVisualBackgroundData(){
    this.saveWearsGlasses();
    this.saveBetterWithGlasses();
    this.saveHasOtherVisualAids();
    this.saveVisualAidsNotes();
    this.saveFormalEvalNotes();
  }
  saveWearsGlasses(){
    this.props.updateSession('wearsGlasses', this.state.wearsGlasses);
  }
  saveBetterWithGlasses(){
    this.props.updateSession('betterWithGlasses', this.state.betterWithGlasses);
  }
  saveHasOtherVisualAids(){
    this.props.updateSession('hasOtherVisualAids', this.state.hasOtherVisualAids);
  }
  saveVisualAidsNotes(){
    this.props.updateSession('visualAidsNotes', this.state.visualAidsNotes);
  }
  saveFormalEvalNotes(){
    this.props.updateSession('formalEvalNotes', this.state.formalEvalNotes);
  }

  saveVisionIssuesData(){
    this.saveVisionIssues();
    this.saveIssuesNotes();
  }
  saveVisionIssues(){
    this.props.updateSession('visionIssues', this.state.visionIssues);
  }
  saveIssuesNotes(){
    this.props.updateSession('issuesNotes', this.state.issuesNotes);
  }

  //called from the child form when the user changes anything
  updateLocalState(key) {
    return evt => {
      const target = evt.target; /* which input has been changed */
      let value = target.type === 'checkbox' ? target.checked: target.value;
      //if setting wearsGlasses to No, reset the value for betterWithGlasses
      // if((key==='wearsGlasses') && (value==='false')){
      //   console.log("clearing betterWIthGlasses");
      //   this.setState({'betterWithGlasses': ''});
      // }
      //handle 'visionIssues' differently, since that holds an array rather than a single value
      if(key === 'visionIssues'){
        const newItem=target.value;
        const newItemIndex = this.state.visionIssues.indexOf(newItem);
        let visionIssues = [...this.state.visionIssues];
        //if an activity is checked: add it, if not already in list
        if((value===true) && (newItemIndex === -1)) {
          visionIssues.push(newItem);
        }
        //activity unchecked: remove it, if already in the list
        else if ((value===false) && (newItemIndex !== -1)){
          visionIssues.splice(newItemIndex, 1);
        }
        value = visionIssues;
      }
      // console.log("Updating EvaluateAbilitiesVisual state with key: " + key + " value: " + value);
      this.setState({ [key]: value });
    }
  }

  //If new props come in after rendering, update the views in this tab.
  //(This occurs when you refresh a view and it pulls data from the database.)
  componentDidUpdate(prevProps, prevState){
    //for values that are Arrays:
    if (_.isEqual(this.props.visionIssues, prevProps.visionIssues) === false){
      console.log("--incoming props: " + this.props.visionIssues + " differ from prev props: " + prevProps.visionIssues);
      this.setState({'visionIssues': this.props.visionIssues});
    };
    //for simple strings
    if (this.props.wearsGlasses !== prevProps.wearsGlasses){
        console.log("--incoming props: " + this.props.wearsGlasses + " differ from prev props: " + prevProps.wearsGlasses);
        this.setState({'wearsGlasses': this.props.wearsGlasses});
    }
    if (this.props.betterWithGlasses !== prevProps.betterWithGlasses){
      console.log("--incoming props: " + this.props.betterWithGlasses + " differ from prev props: " + prevProps.betterWithGlasses);
      this.setState({'betterWithGlasses': this.props.betterWithGlasses});
    }
    if (this.props.hasOtherVisualAids !== prevProps.hasOtherVisualAids){
      console.log("--incoming props: " + this.props.hasOtherVisualAids + " differ from prev props: " + prevProps.hasOtherVisualAids);
      this.setState({'hasOtherVisualAids': this.props.hasOtherVisualAids});
    }
    if (this.props.visualAidsNotes !== prevProps.visualAidsNotes){
      console.log("--incoming props: " + this.props.visualAidsNotes + " differ from prev props: " + prevProps.visualAidsNotes);
      this.setState({'visualAidsNotes': this.props.visualAidsNotes});
    }
    if (this.props.formalEvalNotes !== prevProps.formalEvalNotes){
      console.log("--incoming props: " + this.props.formalEvalNotes + " differ from prev props: " + prevProps.formalEvalNotes);
      this.setState({'formalEvalNotes': this.props.formalEvalNotes});
    }
    if (this.props.issuesNotes !== prevProps.issuesNotes){
      console.log("--incoming props: " + this.props.issuesNotes + " differ from prev props: " + prevProps.issuesNotes);
      this.setState({'issuesNotes': this.props.issuesNotes});
    }
  }

  render() {
    console.log("EvaluateAbilitiesVisual.render()");
    const firstName = this.props.firstName;
    const pronouns = this.props.pronouns;
    const curURL = new URL(window.location.href);
    const searchQuery = curURL.search;

    return(
    <>
    <Switch>
      <Route path={BASE_URL + steps.INTRO}>
        <EvaluateAbilitiesVisualIntro
              key={steps.INTRO}
              firstName={firstName}
              pronouns={pronouns}
              nextSection={NEXT_SECTION}
              onContinue={this.onContinue}
            />
      </Route>
      <Route path={BASE_URL + steps.BACKGROUND}>
        <EvaluateAbilitiesVisualBackground
              key={steps.BACKGROUND}
              firstName={firstName}
              pronouns={pronouns}
              wearsGlasses={this.state.wearsGlasses}
              betterWithGlasses={this.state.betterWithGlasses}
              hasOtherVisualAids={this.state.hasOtherVisualAids}
              visualAidsNotes={this.state.visualAidsNotes}
              formalEvalNotes={this.state.formalEvalNotes}
              onChangeWearsGlasses={this.updateLocalState("wearsGlasses")}
              onChangeBetterWithGlasses={this.updateLocalState("betterWithGlasses")}
              onChangeHasOtherVisualAids={this.updateLocalState("hasOtherVisualAids")}
              onChangeVisualAidsNotes={this.updateLocalState("visualAidsNotes")}
              onChangeFormalEvalNotes={this.updateLocalState("formalEvalNotes")}
              saveFormData={this.saveVisualBackgroundData}
              onContinue={this.onContinue}
            />
      </Route>
      <Route path={BASE_URL + steps.ISSUES}>
        <EvaluateAbilitiesVisualIssues
              key={steps.INTRO}
              firstName={firstName}
              pronouns={pronouns}
              visionIssues={this.state.visionIssues}
              issuesNotes={this.state.issuesNotes}
              onChangeVisionIssues={this.updateLocalState("visionIssues")}
              onChangeIssuesNotes={this.updateLocalState("issuesNotes")}
              saveFormData={this.saveVisionIssuesData}
              onContinue={this.onContinue}
        />
      </Route>
     <Route path={BASE_URL + steps.WRAPUP}>
        <EvaluateAbilitiesVisualWrapup
              key={steps.INTRO}
              firstName={firstName}
              pronouns={pronouns}
              wearsGlasses={this.state.wearsGlasses}
              betterWithGlasses={this.state.betterWithGlasses}
              hasOtherVisualAids={this.state.hasOtherVisualAids}
              formalEvalNotes={this.state.formalEvalNotes}
              visionIssues={this.state.visionIssues}
              issuesNotes={this.state.issuesNotes}
              onContinue={this.onContinue}
        />
      </Route>
      <Route path={BASE_URL}>
        <Redirect to={BASE_URL + steps.INTRO + searchQuery} />
      </Route>
    </Switch>
    </>
    );
  }
}

EvaluateAbilitiesVisual.propTypes = propTypes;
EvaluateAbilitiesVisual.defaultProps = defaultProps;

// withRouter wrapper provides access to the match, location, and history props
// "You can get access to the history object’s properties and the closest <Route>'s match 
// via the withRouter higher-order component. withRouter will pass updated 
// match, location, and history props to the wrapped component whenever it renders"
export default withRouter(EvaluateAbilitiesVisual);
